import { FC, useCallback } from "react";
import { TestimonialSection } from "./component/TestimonialSection";
import { EnterpriseAuthContainer } from "./component/EnterpriseAuthContainer";
import { AuthHeader } from "./component/AuthHeader";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { forgotPasswordSchema, emptyForgotPasswordValue } from "./validation";
import { TbChevronLeft } from "react-icons/tb";
import Button from "../../components/Button";
import InputField from "../../components/InputField";
import useFetch from "../../hooks/useFetch";
import { generateIAMlink } from "../../API/auth";
import { toast } from "react-toastify";

export const ForgetPasswordPage: FC = () => {
  const navigate = useNavigate();
  const { load, isLoading } = useFetch();

  const handleForgetPasswordButton = useCallback(
    async (email: string, resetForm: () => void) => {
      load(generateIAMlink("forgot-pass", email)).then(() => {
        toast.success("Email sent successfully");
        resetForm();

        navigate("/enterprise/verify", {
          state: { email, isForgetPassword: true },
        });
      });
    },
    [load, navigate]
  );

  const {
    errors,
    handleChange,
    values,
    touched,
    handleSubmit,
    isValid,
    dirty,
    resetForm,
  } = useFormik({
    initialValues: emptyForgotPasswordValue,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (): Promise<void> => {
      await handleForgetPasswordButton(values.email, resetForm);
    },
  });

  const isButtonDisabled = !isValid || !dirty;

  return (
    <div className="flex min-h-screen flex-col md:flex-row">
      <EnterpriseAuthContainer>
        <>
          <AuthHeader
            title="Forget Password"
            subtitle="Can’t remember your password? Don’t worry, Fill in your email and we’ll send reset link to begin the process."
          />
          <form className="" onSubmit={handleSubmit}>
            <InputField
              name="email"
              placeholder="Enter Email"
              label="Email"
              error={(touched?.email && errors?.email) || ""}
              value={values.email}
              onChange={handleChange}
            />
            <Button
              text="Send Email"
              className="w-full mt-6"
              type={isButtonDisabled ? "muted" : "primary"}
              disabled={isButtonDisabled || isLoading}
              isLoading={isLoading}
            />
          </form>
          <Link
            to="/enterprise/signin"
            className="flex items-center self-center gap-1 text-center text-sm font-medium font-satoshi text-bblue hover:underline"
          >
            <TbChevronLeft size={16} />
            <p>Back to sign in</p>
          </Link>
        </>
      </EnterpriseAuthContainer>

      <TestimonialSection />
    </div>
  );
};
